import { useCallback, useLayoutEffect, useRef, useMemo } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { useTheme } from "@mui/material/styles"
import Plotly from "plotly.js-dist-min"

import { PLOTLY_THEME_DEFAULTS } from "./constants"
import usePlotlyResizeObserver from "./usePlotlyResizeObserver"
import {
  applyAxisThemeToLayout,
  createThemeLayout,
  deepMerge,
  getPlotlyLayoutColorsFromTheme,
  normalizePlotlyLayout,
} from "./plotly-utils"

export default function PlotlyChart({ data, layout, config, className }) {
  const containerRef = useRef(null)
  const isInitialized = useRef(false)
  const layoutColorsRef = useRef(null)
  const theme = useTheme()
  const layoutColors = useMemo(() => getPlotlyLayoutColorsFromTheme(theme), [theme])

  layoutColorsRef.current = layoutColors

  function isRenderable(container) {
    if (!container || !container.isConnected) {
      return false
    }
    const rect = container.getBoundingClientRect()
    // Guard against zero-size or display:none ancestors
    return rect.width > 0 && rect.height > 0
  }

  const renderPlot = useCallback(async function renderPlotInternal(container) {
    if (!container || !container.isConnected) {
      return
    }

    if (!isRenderable(container)) {
      requestAnimationFrame(() => {
        if (container && container.isConnected) {
          renderPlotInternal(container)
        }
      })
      return
    }

    const incomingLayout = normalizePlotlyLayout(layout)
    const themedLayout = createThemeLayout(container, layoutColorsRef.current)
    const baseLayout = {
      autosize: true,
      margin: {
        l: PLOTLY_THEME_DEFAULTS.margin,
        r: PLOTLY_THEME_DEFAULTS.margin,
        t: PLOTLY_THEME_DEFAULTS.margin,
        b: PLOTLY_THEME_DEFAULTS.margin,
      },
      showlegend: container.offsetWidth > PLOTLY_THEME_DEFAULTS.legendMinWidth,
      ...incomingLayout,
    }
    const chartLayout = applyAxisThemeToLayout(deepMerge(themedLayout, baseLayout), themedLayout.xaxis)

    const chartConfig = {
      responsive: true,
      displaylogo: false,
      modeBarButtonsToRemove: ["pan2d", "select2d", "lasso2d", "toImage"],
      ...(config || {}),
    }

    if (!isInitialized.current) {
      await Plotly.newPlot(container, structuredClone(data), structuredClone(chartLayout), structuredClone(chartConfig))
      isInitialized.current = true
    } else {
      await Plotly.react(container, structuredClone(data), structuredClone(chartLayout), structuredClone(chartConfig))
    }
  }, [data, layout, config])

  const onResize = useCallback((container) => Plotly.Plots.resize(container), [])
  const onLegendVisibilityChange = useCallback(
    (container, showlegend) => Plotly.relayout(container, { showlegend }),
    [],
  )

  usePlotlyResizeObserver({
    containerRef,
    isInitializedRef: isInitialized,
    legendMinWidth: PLOTLY_THEME_DEFAULTS.legendMinWidth,
    onResize,
    onLegendVisibilityChange,
  })
  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }
    void renderPlot(container).catch(() => {})
  }, [layoutColors, renderPlot])

  useLayoutEffect(() => {
    const container = containerRef.current
    return () => {
      try {
        if (container) {
          Plotly.purge(container)
        }
      } catch {
        // no-op on error
      }
      isInitialized.current = false
    }
  }, [])

  return <div ref={containerRef} className={classNames("plotly-chart", className)} />
}

PlotlyChart.propTypes = {
  data: PropTypes.array.isRequired,
  layout: PropTypes.object.isRequired,
  config: PropTypes.object,
  className: PropTypes.string,
}
